import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Check, Crown } from 'lucide-react';
import { toast } from 'sonner';
import useStore from '../store';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';
const API = `${BACKEND_URL}/api`;

function PricingPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { user } = useStore();
  const [loading, setLoading] = useState(null);

  const handleUpgrade = async (tier) => {
    if (!user) {
      navigate('/login');
      return;
    }

    setLoading(tier);

    try {
      const response = await axios.post(
        `${API}/payments/checkout/session`,
        {
          tier: tier,
          origin_url: window.location.origin
        },
        { withCredentials: true }
      );

      window.location.href = response.data.url;
    } catch (error) {
      console.error('Checkout error:', error);
      toast.error('Failed to start checkout');
      setLoading(null);
    }
  };

  const tiers = [
    {
      id: 'free',
      name: t('pricing.free_title'),
      price: t('pricing.free_price'),
      features: [
        t('pricing.feature_members', { count: 1 }),
        'Weekly menu generation',
        'Shopping list with prices',
        'Basic budget tracking',
        'Halal/Kosher filters'
      ]
    },
    {
      id: 'premium',
      name: t('pricing.premium_title'),
      price: t('pricing.premium_price'),
      popular: true,
      features: [
        t('pricing.feature_members', { count: 8 }),
        t('pricing.feature_calorie_tracking'),
        t('pricing.feature_baby_mode'),
        t('pricing.feature_budget_engine'),
        'Advanced dietary filters',
        'Guest dinner planning',
        'Recipe replacements'
      ]
    },
    {
      id: 'unlimited',
      name: t('pricing.unlimited_title'),
      price: t('pricing.unlimited_price'),
      features: [
        'Unlimited family members',
        t('pricing.feature_monthly_planning'),
        'All Premium features',
        'Advanced analytics',
        'Priority support',
        'Custom meal packs',
        'API access'
      ]
    }
  ];

  return (
    <div className="min-h-screen bg-[#FDFBF7] py-12">
      <div className="container mx-auto px-4">
        <div className="text-center mb-12">
          <h1 className="text-5xl font-heading font-bold text-[#2C3E2D] mb-4" data-testid="pricing-title">
            {t('pricing.title')}
          </h1>
          <p className="text-xl text-gray-600">Choose the plan that fits your family</p>
        </div>

        <div className="grid md:grid-cols-3 gap-8 max-w-6xl mx-auto">
          {tiers.map((tier) => (
            <Card
              key={tier.id}
              className={`relative ${
                tier.popular ? 'border-2 border-primary shadow-xl scale-105' : ''
              }`}
              data-testid={`pricing-card-${tier.id}`}
            >
              {tier.popular && (
                <div className="absolute -top-4 left-1/2 transform -translate-x-1/2">
                  <div className="bg-secondary text-white px-4 py-1 rounded-full text-sm font-semibold flex items-center gap-1">
                    <Crown className="w-4 h-4" />
                    Most Popular
                  </div>
                </div>
              )}
              <CardHeader className="text-center pb-8 pt-8">
                <CardTitle className="text-2xl font-bold mb-2">{tier.name}</CardTitle>
                <div className="flex items-baseline justify-center gap-1">
                  <span className="text-4xl font-bold">{tier.price}</span>
                  {tier.id !== 'free' && (
                    <span className="text-gray-600">{t('pricing.per_month')}</span>
                  )}
                </div>
              </CardHeader>
              <CardContent>
                <ul className="space-y-3 mb-6">
                  {tier.features.map((feature, index) => (
                    <li key={index} className="flex items-start gap-2">
                      <Check className="w-5 h-5 text-success shrink-0 mt-0.5" />
                      <span className="text-gray-700">{feature}</span>
                    </li>
                  ))}
                </ul>

                {user?.tier === tier.id ? (
                  <Button
                    variant="outline"
                    className="w-full"
                    disabled
                    data-testid={`current-plan-${tier.id}`}
                  >
                    {t('pricing.current_plan')}
                  </Button>
                ) : tier.id === 'free' ? (
                  <Button
                    variant="outline"
                    className="w-full"
                    onClick={() => navigate('/signup')}
                    data-testid="signup-free-btn"
                  >
                    Get Started
                  </Button>
                ) : (
                  <Button
                    className={`w-full ${
                      tier.popular
                        ? 'bg-primary hover:bg-primary-hover'
                        : 'bg-secondary hover:bg-secondary-hover'
                    } text-white`}
                    onClick={() => handleUpgrade(tier.id)}
                    disabled={loading === tier.id}
                    data-testid={`upgrade-${tier.id}-btn`}
                  >
                    {loading === tier.id ? 'Processing...' : t('pricing.upgrade')}
                  </Button>
                )}
              </CardContent>
            </Card>
          ))}
        </div>

        <div className="text-center mt-12">
          <Button
            variant="ghost"
            onClick={() => navigate('/dashboard')}
            data-testid="back-to-dashboard-pricing-btn"
          >
            Back to Dashboard
          </Button>
        </div>
      </div>
    </div>
  );
}

export default PricingPage;
